(ns ui.editors.card.property-list.view
  (:require
   [common.localization :as loc]
   [common.util :as cu]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.select.select :as select]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.editors.card.property-list.event :as event]
   [ui.editors.card.property-list.sub :as sub]
   ["react-rnd" :as ReactRnd]
   [ui.util :as uu]))

(defn drag-handle
  [{:keys [size] :as _props}]
  (let [size (or size :md)
        container-size (case size
                         :sm 15
                         :md 30
                         :lg 30
                         :xl 40)]
    [:svg {:width (* container-size 0.8) :height container-size}
     (for [row (range 3)
           col (range 2)]
       ^{:key (str "r" row "c" col)}
       [:circle
        {:cx   (+ (* 0.33 container-size) (* 0.2 container-size col))
         :cy   (+ (* 0.33 container-size) (* 0.2 container-size row))
         :r    (* 0.05 container-size)
         :fill "gray"}])]))

(def rnd-primitive (r/adapt-react-class (aget ReactRnd "Rnd")))

(def MAX-CELLS-PER-ROW 4)
(def MAX-TOTAL-CELL-WIDTH 4)

;; Experimental
(defn snap-width
  [width]
  (let [column-width 200]
    (-> (/ width column-width)
        Math/ceil
        (* column-width)
        (max column-width)
        (min (* 4 column-width)))))

(defn on-resize-stop
  [prev-x prev-width delta-width new-x side]
  (let [column-width 200
        new-width (snap-width (+ prev-width delta-width))]
    (if (= side "left")
      (let [new-left (Math/floor (/ new-x column-width))]
        {:x     (* new-left column-width)
         :width new-width})
      (let [new-right (Math/ceil (/ (+ prev-x new-width) column-width))]
        {:x     (- (* new-right column-width) new-width)
         :width new-width}))))

(defn drag-snap-x
  [curr-w new-x]
  (let [column-width 200
        max-columns 4
        max-x (- (* max-columns column-width) curr-w)
        nearest-column (Math/round (/ new-x column-width))]
    (-> (* nearest-column column-width)
        (max 0)
        (min max-x))))

(defn drag-resize-item
  [_props & _children]
  (let [state-atom (r/atom {:can-drag? false})]
    (fn [{:keys [x y width height set-x set-width class]} & children]

      (let [{:keys [can-drag?]} @state-atom]
        [rnd-primitive
         {:bounds          :parent
          :class           (uu/cm "bg-blue-500" class)
          :disableDragging (not can-drag?)
          :dragAxis        :x
          :minWidth        "200px"
          :maxWidth        "800px"
          :position        {:x x
                            :y y}
          :size            {:width  width
                            :height height}

          :enableResizing  {:right (not can-drag?)
                            :left  (not can-drag?)}

          #_:resizeHandleClasses
          #_{:right "absolute right-5 hover:bg-blue-500"
             :left  "absolute left-5 hover:bg-blue-500"}

          :resizeHandleComponent
          {:right
           (r/as-element
            [:div
             {:class (uu/cm "h-full flex flex-col justify-center"
                            "hover:bg-blue-200")}])

           :left
           (r/as-element
            [:div
             {:class (uu/cm "h-full flex flex-col justify-center w-3"
                            "hover:bg-blue-200")}])}
          :onResize        (fn [_e _side _ref _d _pos]
                             ;(js/console.log "on resize " pos)
                             )

          :onResizeStart   (fn [_e _side _ref _d _pos]
                             ;(js/console.log "ON resize start curr w" width " x " x)
                             )

          :onResizeStop    (fn [_e side _ref d pos]
                             ;(js/console.log "resize stop pos " pos " delta " d)
                             (let [delta-width (aget d "width")
                                   new-x (aget pos "x")
                                   side (str side)
                                   snap-pos (on-resize-stop x width delta-width new-x side)
                                   snap-x (:x snap-pos)
                                   snap-width (:width snap-pos)]
                               (set-x snap-x)
                               (set-width snap-width)))

          :onDragStop      (fn [_e data]
                             (let [new-x (aget data "x")
                                   snap-x (drag-snap-x width new-x)]
                               (set-x snap-x)))}

         [:div
          {:class "relative h-full p-7 bg-white"}

          [:div
           {:class          "absolute top-2 left-2 hover:bg-complement-regular rounded-sm"
            :on-mouse-enter #(swap! state-atom assoc :can-drag? true)
            :on-mouse-leave #(swap! state-atom assoc :can-drag? false)}
           [drag-handle {:size :md}]]

          (into [:<>] children)]]))))

(defn grid-editor
  []
  (let [pos-atom (r/atom {:x      0
                          :y      0
                          :width  200
                          :height 200})
        pos2-atom (r/atom {:x      220
                           :y      0
                           :width  200
                           :height 200})
        col-w 200
        num-cols 4
        _max-w (* col-w num-cols)]
    (fn []
      (let [pos @pos-atom
            {:keys [_x _y _width _height]} pos]
        [:div
         {:style {:width  "800px"
                  :height "400px"}}
         [:div
          {:style {:position         "relative"
                   :width            "100%"
                   :height           "100%"
                   :background-color "red"
                   :overflow         :hidden}}

          [drag-resize-item
           (merge
            @pos2-atom
            {:class     "bg-green-500"
             :set-x     #(swap! pos2-atom assoc :x %)
             :set-width #(swap! pos2-atom assoc :width %)})
           [:div
            {:class "w-full h-full bg-white"}
            [:p "Field"]]]]]))))

(defn add-new-cell-button
  [{:keys [on-click _class]}]
  [:div
   {:class "p-0 transition-colors grow flex items-center justify-center gap-5 hover:bg-complement-light-border
                       border border-solid border-base-light-border-paler group rounded min-h-44"}
   [button/secondary-open
    {:on-click on-click}
    [:div
     [:i.fa.fa-plus
      {:class "m-0 text-complement-regular-text text-xl group-hover:text-complement-regular-text-accent"}]]]])

(defn cell-width-selector
  [{:keys [value max set-value] :as _props}]
  (let [current-value value
        cell-width-opts
        (->> [1 2 3 4]
             (mapv (fn [value]
                     {:selected? (= value current-value)
                      :disabled? (< max value)
                      :value     value
                      :label     value})))]
    [:div
     {:class "flex justify-between border rounded overflow-hidden h-6 w-32"}
     (->> cell-width-opts
          (map
           (fn [{:keys [value selected? disabled?]}]
             (let []
               ^{:key (str value)}
               [:div
                {:class    (uu/cm "grow h-full flex items-center justify-center "
                                  "bg-base-light cursor-pointer "
                                  (when-not selected?
                                    "hover:bg-primary-light")
                                  (when disabled?
                                    "pointer-events-none bg-disabled-regular text-disabled-regular-text")
                                  (when selected?
                                    "bg-base-light border-y-0 text-underline"))
                 :on-click (fn []
                             (when-not disabled?
                               (set-value value)))}
                [:span
                 {:class "text-sm m-0"}
                 value]]))))]))

(defn move-cell-buttons
  [{:keys [row-index cell-index last-cell?]}]
  (let [can-move-left? (< 0 cell-index)
        move-cell-horizontally (fn [direction]
                                 (rf/dispatch [::event/move-cell-horizontally row-index cell-index direction]))]
    [:div
     {:class "flex items-center border rounded overflow-hidden"}
     [:button
      {:class    "p-0 shrink-0 w-6 text-base-regular-text disabled:text-disabled-regular-text hover:bg-base-regular disabled:bg-disabled-regular"
       :disabled (not can-move-left?)
       :on-click #(move-cell-horizontally :left)}
      [:i.fas.fa-caret-left]]

     [:button
      {:class    "p-0 shrink-0 w-6 hover:bg-base-regular disabled:text-disabled-regular-text disabled:bg-disabled-regular"
       :on-click #(move-cell-horizontally :right)
       :disabled last-cell?}
      [:i.fas.fa-caret-right]]]))

(defn row-cell-component
  [{:keys [row-cell cell-index row-index max-cell-width-increment last-cell?] :as _props}]
  (let [cell-identifier (str "row-" row-index "-cell-" cell-index)
        field (:field row-cell)
        cell-width-num (:width row-cell)
        always-visible? (:always-visible? row-cell)
        empty-cell-value (:empty-cell-value row-cell)
        available-options @(rf/subscribe [::sub/available-field-options field])
        set-cell-field (fn [field]
                         (rf/dispatch [::event/set-cell-field row-index cell-index field]))
        remove-row-cell (fn [_]
                          (rf/dispatch [::event/remove-card-row-cell row-index cell-index]))
        set-cell-always-visible (fn [always-visible?]
                                  (rf/dispatch [::event/set-cell-always-visible row-index cell-index always-visible?]))
        set-empty-cell-value (fn [e]
                               (rf/dispatch-sync [::event/set-cell-empty-cell-value row-index cell-index (dg/event-target-value e)]))]
    [:div
     {:class (uu/cm "flex h-full flex-col gap-3 border p-2 rounded-sm")}
     [:div
      {:class "flex justify-between gap-2"}
      [:div
       {:class "grow"}
       [select/view
        {:value     (->> available-options
                         (cu/filter-by :value field)
                         (first))
         :options   available-options
         :on-change set-cell-field}]]

      [button/secondary-open
       {:on-click remove-row-cell}
       [:i.fal.fa-trash]]]

     [:div
      {:class "flex flex-col gap-4"}
      [:div
       {:class "flex justify-between w-full"}
       [:label
        {:for   (str cell-identifier "-always-visible")
         :class "text-sm m-0 w-28 font-medium"}
        (loc/tr :card-editor/always-visible)]

       [:div
        {:class "inline-flex"}
        [toggle/toggle
         {:id        (str cell-identifier "-always-visible")
          :checked?  always-visible?
          :on-change set-cell-always-visible}]]]

      (when always-visible?
        [:<>
         [:div
          {:class "flex justify-between gap-2"}
          [:label
           {:for   (str cell-identifier "-always-visible")
            :class "text-sm m-0 w-28 font-medium"}
           (loc/tr :card-editor/empty-cell-value)]

          [:input
           {:size      1
            :class     "max-w-32 h-6"
            :value     empty-cell-value
            :on-change set-empty-cell-value}]]])

      [:div
       {:class "flex justify-between gap-2"}
       [:p
        {:class "text-sm m-0 w-28 font-medium"}
        (loc/tr :card-editor/cell-width)]
       [cell-width-selector
        {:value     cell-width-num
         :max       (+ cell-width-num max-cell-width-increment)
         :set-value (fn [new-width]
                      (rf/dispatch [::event/set-cell-width row-index cell-index new-width]))}]
       [:div
        {:class "flex justify-start"}
        [move-cell-buttons
         {:row-index  row-index
          :cell-index cell-index
          :last-cell? last-cell?}]]]]]))


(defn card-row-component
  [row-index]
  (let [row @(rf/subscribe [::sub/card-row row-index])
        cells (:cells row)
        cell-count (count cells)
        last-cell-index (dec cell-count)
        total-cells-width (->> cells
                               (mapv :width)
                               (reduce +))
        available-width (- MAX-TOTAL-CELL-WIDTH total-cells-width)
        can-add-cell? (and (< cell-count MAX-CELLS-PER-ROW)
                           (< 0 available-width))

        add-card-row-cell (fn [_]
                            (rf/dispatch [::event/add-card-row-cell row-index]))]
    [:div
     {:class "grid grid-cols-4 grid-flow-row gap-2 items-stretch"}
     (->> cells
          (map-indexed
           (fn [cell-index row-cell]
             (let [cell-width (:width row-cell)]
               ^{:key (str "r-" row-index "-c-" cell-index)}
               [:div
                {:class (uu/cm "shrink-0"
                               (case cell-width
                                 1 "col-span-1"
                                 2 "col-span-2"
                                 3 "col-span-3"
                                 4 "col-span-4"
                                 "auto"))}
                [row-cell-component
                 {:row-cell                 row-cell
                  :cell-index               cell-index
                  :row-index                row-index
                  :last-cell?               (= cell-index last-cell-index)
                  :max-cell-width-increment available-width}]]))))
     (when can-add-cell?
       [:div
        {:class "col-span-1 flex"}
        [add-new-cell-button
         {:class    "h-full grow w-full hover:bg-complement-light-border transition-colors"
          :on-click add-card-row-cell}]])]))

(defn container
  []
  (let [row-count @(rf/subscribe [::sub/card-row-count])
        add-card-row (fn []
                       (rf/dispatch [::event/add-card-row]))]
    [:div
     {:class "relative space-y-2 rounded p-5 shadow shadow-lg border overflow-hidden"}
     [:div
      {:class "flex flex-col space-y-2"}
      (->> (range row-count)
           (map (fn [row-index]
                  ^{:key (str "editor-row-" row-index)}
                  [card-row-component row-index])))]

     [:div
      {:class "grid grid-cols-4 grid-flow-row gap-2"}
      [:div
       {:class "min-h-32 col-span-1 flex flex-col "}
       [add-new-cell-button
        {:on-click add-card-row}]]]]))

(defn view
  []
  [:div
   {:class "flex flex-col gap-5 pt-3"}
   [:div
    [:p
     {:class "text-lg font-medium"}
     (loc/tr :card-editor/card-content-label)]
    [:p
     {:class "text-base-light-text-paler text-sm m-0"}
     (loc/tr :card-editor/card-content-helper)]]
   [:div
    {:class "flex flex-col"}
    [container]]])
